import { Inject, Injectable, Optional } from '@angular/core';
import { STATSIG_INIT_CONFIG, StatsigService } from '@statsig/angular-bindings';
import { StatsigClient } from '@statsig/js-client';
import { CarDetail, Price } from '../../car/shared/car-detail';
import { SessionService } from '../session.service';
import { buildStatsigUser } from './statsig-environment';

type StatsigInitConfig = {
  client?: StatsigClient;
  sdkKey: string;
  user: object;
  options: object;
};

@Injectable({
  providedIn: 'root'
})
export class StatsigEventsService {

  constructor(
    private statsigService: StatsigService,
    private sessionService: SessionService,
    @Inject(STATSIG_INIT_CONFIG) @Optional() private statsigInitConfig?: StatsigInitConfig
  ) { }

  private get sessionData() {
    return this.sessionService.getSessionData();
  }

  private baseMetadata(): Record<string, string> {
    return {
      event_source: 'client',
      product_type: 'Car',
      event_timestamp: new Date().toISOString()
    };
  }

  private buildCarDetailMetadata(carDetail?: CarDetail): Record<string, string> {
    const metadata: Record<string, string> = {};
    if (!carDetail) {
      return metadata;
    }

    if (carDetail.companyName) {
      metadata['car_company'] = carDetail.companyName;
    }
    if (carDetail.carModelName) {
      metadata['car_model'] = carDetail.carModelName;
    }
    if (carDetail.carCategory) {
      metadata['car_category'] = carDetail.carCategory;
    }
    if (carDetail.carType) {
      metadata['car_type'] = carDetail.carType;
    }
    if (carDetail.vehicleType) {
      metadata['vehicle_type'] = carDetail.vehicleType;
    }
    if (carDetail.vendorCode) {
      metadata['vendor_code'] = carDetail.vendorCode;
    }
    if (carDetail.numberDays != null) {
      metadata['rental_days'] = String(carDetail.numberDays);
    }
    if (carDetail.totalRate?.amount != null) {
      metadata['total_rate'] = String(carDetail.totalRate.amount);
    }
    if (carDetail.totalRate?.currencyCode) {
      metadata['total_rate_currency'] = carDetail.totalRate.currencyCode;
    }
    if (carDetail.dayRate?.amount != null) {
      metadata['day_rate'] = String(carDetail.dayRate.amount);
    }
    if (carDetail.pickUpLocation?.locationName) {
      metadata['car_pickup_location'] = carDetail.pickUpLocation.locationName;
    }
    if (carDetail.returnLocation?.locationName) {
      metadata['car_return_location'] = carDetail.returnLocation.locationName;
    }

    return metadata;
  }

  /** True only when bootstrap registered a fully initialized Statsig client. */
  private isStatsigReady(): boolean {
    return !!this.statsigInitConfig?.client;
  }

  private logEvent(eventName: string, metadata?: Record<string, string>): void {
    if (!this.isStatsigReady()) {
      return;
    }

    try {
      this.statsigService.logEvent(eventName, this.sessionData?.correlationId, metadata ?? this.baseMetadata());
    } catch (error) {
      console.warn(`Statsig event "${eventName}" was not recorded.`, error);
    }
  }

  syncUser(): void {
    if (!this.isStatsigReady()) {
      return;
    }

    try {
      const user = buildStatsigUser(this.sessionData);
      void this.statsigService.updateUserAsync(user).catch(error => {
        console.warn('Statsig user sync failed.', error);
      });
    } catch (error) {
      console.warn('Statsig user sync failed.', error);
    }
  }

  logSearchSubmitted(
    pickUpLocationName?: string,
    dropOffLocationName?: string,
    pickUpDateTime?: string,
    dropOffDateTime?: string
  ): void {
    const metadata = this.baseMetadata();
    if (pickUpLocationName) metadata['pickup_location'] = pickUpLocationName;
    if (dropOffLocationName) metadata['dropoff_location'] = dropOffLocationName;
    if (pickUpDateTime) metadata['pickup_datetime'] = pickUpDateTime;
    if (dropOffDateTime) metadata['dropoff_datetime'] = dropOffDateTime;

    this.logEvent('ngcar_search_submitted', metadata);
  }

  logSearchViewed(searchResultsCount: number): void {
    const metadata = this.baseMetadata();
    metadata['search_results'] = String(searchResultsCount ?? 0);
    this.logEvent('ngcar_search_viewed', metadata);
  }

  logDetailsViewed(carDetail: CarDetail): void {
    const metadata = {
      ...this.baseMetadata(),
      ...this.buildCarDetailMetadata(carDetail)
    };
    this.logEvent('ngcar_details_viewed', metadata);
  }

  logCheckoutStarted(): void {
    this.logEvent('ngcar_checkout_started');
  }

  logBookingCompleted(bookingId: string, carDetail?: CarDetail, amountPaid?: Price): void {
    const metadata = {
      ...this.baseMetadata(),
      ...this.buildCarDetailMetadata(carDetail)
    };
    if (bookingId) {
      metadata['booking_id'] = bookingId;
    }
    if (amountPaid?.amount != null) {
      metadata['amount_paid'] = String(amountPaid.amount);
    }
    if (amountPaid?.currencyCode) {
      metadata['amount_paid_currency'] = amountPaid.currencyCode;
    }
    this.logEvent('ngcar_booking_completed', metadata);
  }
}
